import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { History, Filter, ChevronLeft, ChevronRight, Lock, RefreshCw, ChevronDown, ChevronUp, Download } from 'lucide-react';

const ACTION_COLORS = {
  create: 'bg-emerald-100 text-emerald-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
  reset: 'bg-amber-100 text-amber-700',
  seed: 'bg-purple-100 text-purple-700',
  recompute: 'bg-indigo-100 text-indigo-700',
  reset_password: 'bg-orange-100 text-orange-700',
};

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return iso; }
};

const PAGE_SIZE = 50;

const AdminAuditLogs = () => {
  const { hasPermission } = useAuth();
  const canView = hasPermission && hasPermission('admin_audit_log', 'view');

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [expanded, setExpanded] = useState({});

  // Filtres
  const [filterModule, setFilterModule] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const load = useCallback(async (newOffset = 0) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: newOffset };
      if (filterModule) params.module = filterModule;
      if (filterAction) params.action = filterAction;
      if (filterUser) params.user_email = filterUser;
      if (filterFrom) params.date_from = filterFrom;
      if (filterTo) params.date_to = filterTo;
      const { data } = await api.get('/admin/audit-log', { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setOffset(newOffset);
      setExpanded({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement du journal');
    } finally {
      setLoading(false);
    }
  }, [filterModule, filterAction, filterUser, filterFrom, filterTo]);

  const loadModules = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/audit-log/modules');
      setModules(data.modules || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!canView) return;
    loadModules();
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const handleApplyFilters = () => load(0);
  const handleResetFilters = () => {
    setFilterModule(''); setFilterAction(''); setFilterUser('');
    setFilterFrom(''); setFilterTo('');
    setTimeout(() => load(0), 0);
  };

  const handleExportCsv = async () => {
    try {
      const params = {};
      if (filterModule) params.module = filterModule;
      if (filterAction) params.action = filterAction;
      if (filterUser) params.user_email = filterUser;
      if (filterFrom) params.date_from = filterFrom;
      if (filterTo) params.date_to = filterTo;
      const resp = await api.get('/admin/audit-log/export.csv', { params, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `audit-log_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur export CSV');
    }
  };

  if (!canView) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="admin-audit-denied">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3">
          <Lock className="w-6 h-6 text-red-600" />
          <div>
            <h2 className="font-semibold text-red-900">Accès refusé</h2>
            <p className="text-sm text-red-800 mt-1">
              Permission <code>admin_audit_log.view</code> requise.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6" data-testid="admin-audit-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <History className="w-6 h-6 text-blue-600" />
            Journal des modifications administrateur
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Historique de toutes les actions de configuration (création, modification, suppression).
          </p>
        </div>
        <div className="flex gap-2 self-start">
          <button
            type="button"
            onClick={handleExportCsv}
            className="inline-flex items-center gap-1 text-sm px-3 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
            data-testid="btn-export-csv"
          >
            <Download className="w-4 h-4" /> Exporter CSV
          </button>
          <button
            type="button"
            onClick={() => load(offset)}
            className="inline-flex items-center gap-1 text-sm px-3 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            data-testid="btn-refresh-audit"
          >
            <RefreshCw className="w-4 h-4" /> Rafraîchir
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-gray-700">
          <Filter className="w-4 h-4" /> Filtres
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Module</label>
            <select
              value={filterModule}
              onChange={e => setFilterModule(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
              data-testid="filter-module"
            >
              <option value="">Tous</option>
              {modules.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Action</label>
            <select
              value={filterAction}
              onChange={e => setFilterAction(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
              data-testid="filter-action"
            >
              <option value="">Toutes</option>
              <option value="create">create</option>
              <option value="update">update</option>
              <option value="delete">delete</option>
              <option value="reset">reset</option>
              <option value="seed">seed</option>
              <option value="recompute">recompute</option>
              <option value="reset_password">reset_password</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Utilisateur (email)</label>
            <input
              type="text"
              value={filterUser}
              onChange={e => setFilterUser(e.target.value)}
              placeholder="ex. admin@ticfe.fr"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
              data-testid="filter-user"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Du</label>
            <input
              type="datetime-local"
              value={filterFrom}
              onChange={e => setFilterFrom(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
              data-testid="filter-from"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Au</label>
            <input
              type="datetime-local"
              value={filterTo}
              onChange={e => setFilterTo(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
              data-testid="filter-to"
            />
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={handleApplyFilters}
            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
            data-testid="btn-apply-filters"
          >
            Appliquer
          </button>
          <button
            type="button"
            onClick={handleResetFilters}
            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
            data-testid="btn-reset-filters"
          >
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left w-44">Horodatage</th>
              <th className="px-3 py-2 text-left">Utilisateur</th>
              <th className="px-3 py-2 text-left w-40">Module</th>
              <th className="px-3 py-2 text-left w-28">Action</th>
              <th className="px-3 py-2 text-left">Détail</th>
              <th className="px-3 py-2 text-right w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-testid="tbody-audit">
            {loading ? (
              <tr><td colSpan="6" className="px-4 py-6 text-center text-gray-500">Chargement…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan="6" className="px-4 py-6 text-center text-gray-500" data-testid="empty-audit">Aucun événement.</td></tr>
            ) : items.map((it) => (
              <React.Fragment key={it.id}>
                <tr
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => setExpanded({ ...expanded, [it.id]: !expanded[it.id] })}
                  data-testid={`audit-row-${it.id}`}
                >
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{formatDate(it.timestamp)}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900 text-xs">{it.user_email || '-'}</div>
                    <div className="text-[10px] text-gray-500">{it.user_role}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{it.module}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${ACTION_COLORS[it.action] || 'bg-gray-100 text-gray-700'}`}>
                      {it.action}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-700">{it.summary}</td>
                  <td className="px-3 py-2 text-right text-gray-400">
                    {expanded[it.id] ? <ChevronUp className="w-4 h-4 inline" /> : <ChevronDown className="w-4 h-4 inline" />}
                  </td>
                </tr>
                {expanded[it.id] && (
                  <tr className="bg-gray-50/50">
                    <td colSpan="6" className="px-4 py-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                        <div>
                          <div className="font-semibold text-gray-700 mb-1">Avant</div>
                          <pre className="bg-white border border-gray-200 rounded p-2 overflow-auto max-h-60 text-[11px]">
                            {it.before ? JSON.stringify(it.before, null, 2) : '—'}
                          </pre>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-700 mb-1">Après</div>
                          <pre className="bg-white border border-gray-200 rounded p-2 overflow-auto max-h-60 text-[11px]">
                            {it.after ? JSON.stringify(it.after, null, 2) : '—'}
                          </pre>
                        </div>
                      </div>
                      {it.entity_id && (
                        <div className="mt-2 text-xs text-gray-500">
                          Entité : <span className="font-mono">{it.entity_id}</span>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <div data-testid="audit-total">
          {total} événement{total > 1 ? 's' : ''} — page {currentPage} / {totalPages}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={offset <= 0 || loading}
            onClick={() => load(Math.max(0, offset - PAGE_SIZE))}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
            data-testid="btn-prev-page"
          >
            <ChevronLeft className="w-4 h-4" /> Précédent
          </button>
          <button
            type="button"
            disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => load(offset + PAGE_SIZE)}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
            data-testid="btn-next-page"
          >
            Suivant <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminAuditLogs;
